import {
  useGetMentorStatisticsByCoursesCanTeach,
  useGetMentorStatisticsByMentorStatus,
  useGetMentorStatisticsByUniversity,
  useGetMentorStatisticsByUniversityRegion,
  useGetStudentStatisticsByApplicationStatus,
  useGetStudentStatisticsByEnrollments,
  useGetStudentStatisticsByIntendedMajors,
  useGetStudentStatisticsByUniversity,
  useGetStudentStatisticsByUniversityAdmissionRate,
  useGetStudentStatisticsByUniversityApplicationsOpened,
  useGetStudentStatisticsByUniversityApplicationsOpenedCountry,
  useGetStudentStatisticsByUniversityApplicationsOpenedRegion,
  useGetStudentStatisticsByUniversityOffersAccepted,
  useGetUserStatisticsByCity,
  useGetUserStatisticsByCountry,
  useGetUserStatisticsByProfileCompletion,
  useDownloadStudentApplicationsCSV,
} from '@actions';
import Button from '@components/V4/Button';
import { useAuthState } from '@contexts/AuthContext';
import {
  MENTOR_PROFILE_COMPLETION_FIELDS,
  ProfileCompletionFieldsType,
  STUDENT_PROFILE_ACADEMIC_COMPLETION_FIELDS,
  STUDENT_PROFILE_DEMOGRAPHIC_COMPLETION_FIELDS,
} from '@shared/constants';
import { UserFieldParam, UserRole } from '@shared/enums';
import useToast from 'apps/agora/src/hooks/useToast';
import { useState, useRef } from 'react';
import { logger } from '../../../../utils/logger';
import ProfilesBox from './ProfilesBox';
import ProfilesModal, { ProfilesPages } from './ProfilesModal/ProfilesModal';

const subjectData = [
  { name: 'Maths', value: 100 },
  { name: 'Law', value: 80 },
  { name: 'Chemistry', value: 60 },
  { name: 'Physics', value: 40 },
  { name: 'Languages', value: 20 },
  { name: 'Business', value: 10 },
];

interface ModalPageData {
  modalPage?: ProfilesPages;
  param?: string;
  paramName?: string;
  role?: UserRole;
  country?: string;
  fields?: ProfileCompletionFieldsType[];
}

interface ActivityState {
  name: string;
  isCurrent: boolean;
  isActive?: boolean;
}

const Profiles = () => {
  const { roles } = useAuthState();
  const canExport = roles.includes(UserRole.Financial) || roles.includes(UserRole.SuperUser);
  const toast = useToast();
  const [activeStates, setActiveStates] = useState<ActivityState[]>([
    { name: 'applicationStatus', isCurrent: false },
    { name: 'intendedMajors', isCurrent: false },
    { name: 'enrollments', isCurrent: false, isActive: false },
  ]);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<ModalPageData>({});
  const downloadCSVRef = useRef<() => Promise<any>>();
  const { data: studentStatisticsByCity, isLoading: isLoadingStudentCity } =
    useGetUserStatisticsByCity({
      role: UserRole.Student,
      param: UserFieldParam.City,
    });

  const currentActive = activeStates.find((a) => a.isCurrent);

  const getIsActive = (name: string) =>
    activeStates.find((a) => a.name === name)?.isActive ?? false;

  const { data: mentorStatisticsByMentorStatus, isLoading: isLoadingMentorStatus } =
    useGetMentorStatisticsByMentorStatus();
  const { data: mentorStatisticsByCoursesCanTeach, isLoading: isLoadingMentorCourses } =
    useGetMentorStatisticsByCoursesCanTeach();
  const { data: mentorStatisticsByUniversity, isLoading: isLoadingMentorUniversity } =
    useGetMentorStatisticsByUniversity();
  const { data: mentorStatisticsByUniversityRegion, isLoading: isLoadingMentorRegion } =
    useGetMentorStatisticsByUniversityRegion();
  const { data: studentStatisticsByUniversity, isLoading: isLoadingStudentUniversity } =
    useGetStudentStatisticsByUniversity();
  const { data: studentStatisticsByUniversityOffersAccepted, isLoading: isLoadingStudentOffers } =
    useGetStudentStatisticsByUniversityOffersAccepted();
  const {
    data: studentStatisticsByUniversityApplicationsOpened,
    isLoading: isLoadingStudentApplications,
  } = useGetStudentStatisticsByUniversityApplicationsOpened();
  const {
    data: studentStatisticsByUniversityApplicationsOpenedRegion,
    isLoading: isLoadingStudentRegion,
  } = useGetStudentStatisticsByUniversityApplicationsOpenedRegion();
  const {
    data: studentStatisticsByUniversityApplicationsOpenedCountry,
    isLoading: isLoadingStudentApplicationsCountry,
  } = useGetStudentStatisticsByUniversityApplicationsOpenedCountry();
  const { data: studentStatisticsByUniversityAdmissionRate, isLoading: isLoadingStudentAdmission } =
    useGetStudentStatisticsByUniversityAdmissionRate();
  const { data: studentStatisticsByEnrollments, isLoading: isLoadingEnrollments } =
    useGetStudentStatisticsByEnrollments({
      isActive: getIsActive('enrollments'),
    });
  const { data: studentStatisticsByApplicationStatus, isLoading: isLoadingApplicationStatus } =
    useGetStudentStatisticsByApplicationStatus({});
  const { data: studentStatisticsByIntendedMajors, isLoading: isLoadingIntendedMajors } =
    useGetStudentStatisticsByIntendedMajors({});
  const { data: mentorStatisticsByProfileCompletion, isLoading: isLoadingMentorProfile } =
    useGetUserStatisticsByProfileCompletion({
      role: UserRole.Mentor,
      fields: MENTOR_PROFILE_COMPLETION_FIELDS,
    });
  const {
    data: studentDemographicStatisticsByProfileCompletion,
    isLoading: isLoadingStudentDemographic,
  } = useGetUserStatisticsByProfileCompletion({
    role: UserRole.Student,
    fields: STUDENT_PROFILE_DEMOGRAPHIC_COMPLETION_FIELDS,
  });
  const {
    data: studentAcademicStatisticsByProfileCompletion,
    isLoading: isLoadingStudentAcademic,
  } = useGetUserStatisticsByProfileCompletion({
    role: UserRole.Student,
    fields: STUDENT_PROFILE_ACADEMIC_COMPLETION_FIELDS,
  });
  const { data: mentorStatisticsByCountry, isLoading: isLoadingMentorCountry } =
    useGetUserStatisticsByCountry({
      role: UserRole.Mentor,
      param: UserFieldParam.Country,
    });
  const { data: studentStatisticsByCountry, isLoading: isLoadingStudentCountry } =
    useGetUserStatisticsByCountry({
      role: UserRole.Student,
      param: UserFieldParam.Country,
    });

  const { refetch: downloadCSV } = useDownloadStudentApplicationsCSV();

  const downloadCSVHandler = async () => {
    try {
      const response = await downloadCSV();
      if (response?.data?.csvData) {
        const blob = new Blob([response.data.csvData], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'student-applications.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      logger.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV file');
    }
  };

  const ensureActivityExists = (name: string) => {
    setActiveStates((prev) => {
      const exists = prev.some((a) => a.name === name);
      return exists ? prev : [...prev, { name, isActive: false, isCurrent: false }];
    });
  };

  const setCurrentActivity = (name: string) => {
    ensureActivityExists(name);
    setActiveStates((prev) =>
      prev.map((a) => (a.name === name ? { ...a, isCurrent: true } : { ...a, isCurrent: false }))
    );
  };

  const toggleShowOnlyActiveHandler = (name: string) => {
    setActiveStates((prev) => {
      const exists = prev.some((a) => a.name === name);
      return exists
        ? prev.map((a) =>
            a.name === name
              ? { ...a, isActive: !a.isActive, isCurrent: true }
              : { ...a, isCurrent: false }
          )
        : [
            ...prev.map((a) => ({ ...a, isCurrent: false })),
            { name, isActive: true, isCurrent: true },
          ];
    });
  };

  const toggleShowModalHandler = (
    modalPage?: ProfilesPages,
    param?: string,
    paramName?: string,
    role?: UserRole,
    fields?: ProfileCompletionFieldsType[]
  ) => {
    if (!modalPage) return;

    setModalData({ modalPage, param, paramName, role, fields });
    setShowModal((prev) => !prev);
    if (param) setCurrentActivity(param);
  };

  const onCloseModalHanlder = () => {
    setModalData({});
    setShowModal(false);
  };

  return (
    <div className="flex flex-col justify-center w-full h-full p-8">
      <ProfilesRow title="Mentors">
        <div className="w-full flex flex-col mt-8">
          <div className="text-lg font-bold text-white w-full mb-4 font-raleway">Coverage</div>
          <div className="grid gap-5 grid-cols-3 w-full">
            <ProfilesBox
              title="Mentors per Region"
              data={mentorStatisticsByUniversityRegion || []}
              type="piechart"
              modalPage={ProfilesPages.MENTORS_BY_UNIVERSITY_REGION}
              onBarClick={toggleShowModalHandler}
              isLoading={isLoadingMentorRegion}
            />
            <ProfilesBox
              title="Mentors By Country"
              data={mentorStatisticsByCountry || []}
              modalPage={ProfilesPages.USERS_BY_COUNTRY}
              onBarClick={toggleShowModalHandler}
              role={UserRole.Mentor}
              isLoading={isLoadingMentorCountry}
            />
            <ProfilesBox
              title="Mentors By Graduated University"
              data={mentorStatisticsByUniversity || []}
              modalPage={ProfilesPages.MENTORS_BY_UNIVERSITY}
              onBarClick={toggleShowModalHandler}
              color="#275CA5"
              isLoading={isLoadingMentorUniversity}
            />
          </div>
        </div>
        <div className="w-full flex gap-5 justify-between mt-8">
          <div className="w-full h-full">
            <div className="text-lg font-bold text-white w-full mb-4 font-raleway">&nbsp;</div>
            <div className="grid gap-5 w-full grid-cols-3">
              <ProfilesBox
                title="Courses they can teach"
                data={mentorStatisticsByCoursesCanTeach || []}
                modalPage={ProfilesPages.MENTORS_BY_COURSES_CAN_TEACH}
                onBarClick={toggleShowModalHandler}
                isLoading={isLoadingMentorCourses}
              />
              <ProfilesBox
                title="Mentors Profile Completion Issues"
                data={mentorStatisticsByProfileCompletion || []}
                color="#D85E62"
                isNormalized={false}
                modalPage={ProfilesPages.USERS_BY_PROFILE_COMPLETION}
                onBarClick={toggleShowModalHandler}
                role={UserRole.Mentor}
                fields={MENTOR_PROFILE_COMPLETION_FIELDS}
                isLoading={isLoadingMentorProfile}
              />
              <ProfilesBox
                title="Mentors By Status"
                data={mentorStatisticsByMentorStatus || []}
                type="piechart"
                shouldShowAllLink
                modalPage={ProfilesPages.MENTORS_BY_STATUS}
                onBarClick={toggleShowModalHandler}
                onShowModalClick={() =>
                  toggleShowModalHandler(
                    ProfilesPages.MENTORS_BY_STATUS,
                    mentorStatisticsByMentorStatus && mentorStatisticsByMentorStatus.length > 0
                      ? mentorStatisticsByMentorStatus[0].name
                      : undefined
                  )
                }
                isLoading={isLoadingMentorStatus}
              />
            </div>
          </div>
        </div>
      </ProfilesRow>
      <ProfilesRow title="Students">
        <div className="w-full flex flex-col mt-8">
          <div className="text-lg font-bold text-white w-full mb-4 font-raleway">
            Distribution By:
          </div>
          <div className="grid gap-5 grid-cols-3 w-full">
            <ProfilesBox
              title="Regions of Interest (Students applied in)"
              data={studentStatisticsByUniversityApplicationsOpenedRegion || []}
              modalPage={ProfilesPages.STUDENTS_BY_UNIVERSITY_APPLICATIONS_OPENED_REGION}
              onBarClick={toggleShowModalHandler}
              role={UserRole.Student}
              type="piechart"
              isLoading={isLoadingStudentRegion}
            />
            <ProfilesBox
              title="Students By Home Country"
              data={studentStatisticsByCountry || []}
              modalPage={ProfilesPages.USERS_BY_COUNTRY}
              onBarClick={toggleShowModalHandler}
              role={UserRole.Student}
              isLoading={isLoadingStudentCountry}
            />
            <ProfilesBox
              title="Students By Home City"
              data={studentStatisticsByCity || []}
              modalPage={ProfilesPages.USERS_BY_CITY}
              onBarClick={toggleShowModalHandler}
              role={UserRole.Student}
              isLoading={isLoadingStudentCity}
            />
            <ProfilesBox
              title="Countries by Number of Applications"
              data={studentStatisticsByUniversityApplicationsOpenedCountry || []}
              modalPage={ProfilesPages.STUDENTS_BY_UNIVERSITY_APPLICATIONS_OPENED_COUNTRY}
              onBarClick={toggleShowModalHandler}
              role={UserRole.Student}
              isLoading={isLoadingStudentApplicationsCountry}
            />
            <ProfilesBox
              title="Total Student Applications by University"
              data={studentStatisticsByUniversity || []}
              modalPage={ProfilesPages.STUDENTS_BY_UNIVERSITY}
              onBarClick={toggleShowModalHandler}
              color="#275CA5"
              isLoading={isLoadingStudentUniversity}
            />
            <ProfilesBox
              title="Courses Enrolled In"
              data={studentStatisticsByEnrollments || []}
              modalPage={ProfilesPages.STUDENTS_BY_ENROLLMENTS}
              onBarClick={toggleShowModalHandler}
              hasActiveToggle
              onToggle={() => toggleShowOnlyActiveHandler('enrollments')}
              showOnlyActive={getIsActive('enrollments')}
              isLoading={isLoadingEnrollments}
            />
          </div>
        </div>
        <div className="w-full flex gap-5 justify-between mt-8">
          <div className="w-full">
            <div className="text-lg font-bold text-white w-full mb-4 font-raleway flex justify-between items-center">
              Results
              {canExport ? (
                <Button
                  onClick={downloadCSVHandler}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                  buttonText="Download CSV"
                />
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-5 w-full">
              <ProfilesBox
                title="By Application Status"
                data={studentStatisticsByApplicationStatus || []}
                modalPage={ProfilesPages.STUDENTS_BY_APPLICATION_STATUS}
                onBarClick={toggleShowModalHandler}
                isLoading={isLoadingApplicationStatus}
              />
              <ProfilesBox
                title="By Intended Major"
                data={studentStatisticsByIntendedMajors || []}
                modalPage={ProfilesPages.STUDENTS_BY_INTENDED_MAJOR}
                onBarClick={toggleShowModalHandler}
                isLoading={isLoadingIntendedMajors}
              />
            </div>
          </div>
          <div className="w-full">
            <div className="text-lg font-bold text-white w-full mb-4 font-raleway">
              Profile Completion:
            </div>
            <div className="grid grid-cols-2 gap-5 w-full">
              <ProfilesBox
                title="Demographic Student Data"
                data={studentDemographicStatisticsByProfileCompletion || []}
                color="#D85E62"
                isNormalized={false}
                modalPage={ProfilesPages.USERS_BY_PROFILE_COMPLETION}
                onBarClick={toggleShowModalHandler}
                role={UserRole.Student}
                fields={STUDENT_PROFILE_DEMOGRAPHIC_COMPLETION_FIELDS}
                isLoading={isLoadingStudentDemographic}
              />
              <ProfilesBox
                title="Results / Application Data"
                data={studentAcademicStatisticsByProfileCompletion || []}
                color="#D85E62"
                isNormalized={false}
                modalPage={ProfilesPages.USERS_BY_PROFILE_COMPLETION}
                onBarClick={toggleShowModalHandler}
                role={UserRole.Student}
                fields={STUDENT_PROFILE_ACADEMIC_COMPLETION_FIELDS}
                isLoading={isLoadingStudentAcademic}
              />
            </div>
          </div>
        </div>
      </ProfilesRow>
      <ProfilesRow title="Universities">
        <div className="w-full flex flex-col mt-8">
          <div className="grid gap-5 grid-cols-3 w-full">
            <ProfilesBox
              title="By Currently Opened / Active Applications"
              data={studentStatisticsByUniversityApplicationsOpened || []}
              modalPage={ProfilesPages.STUDENTS_BY_UNIVERSITY_APPLICATIONS_OPENED}
              onBarClick={toggleShowModalHandler}
              color="#275CA5"
              isLoading={isLoadingStudentApplications}
            />
            <ProfilesBox
              title="By Admission Rate"
              data={studentStatisticsByUniversityAdmissionRate || []}
              modalPage={ProfilesPages.STUDENTS_BY_UNIVERSITY_ADMISSION_RATE}
              onBarClick={toggleShowModalHandler}
              color="#275CA5"
              receivesDataAsPercentages
              isLoading={isLoadingStudentAdmission}
            />
            <ProfilesBox
              title="By Offers Accepted"
              data={studentStatisticsByUniversityOffersAccepted || []}
              modalPage={ProfilesPages.STUDENTS_BY_UNIVERSITY_OFFERS_ACCEPTED}
              onBarClick={toggleShowModalHandler}
              color="#275CA5"
              isLoading={isLoadingStudentOffers}
            />
          </div>
        </div>
      </ProfilesRow>

      <ProfilesModal
        modalPage={modalData.modalPage}
        shouldShowModal={showModal}
        onClose={onCloseModalHanlder}
        param={modalData.param}
        paramName={modalData.paramName}
        role={modalData.role}
        fields={modalData.fields}
        showOnlyActive={!!currentActive?.isActive}
        onToggle={() => currentActive && toggleShowOnlyActiveHandler(currentActive.name)}
      />
    </div>
  );
};

interface ProfilesRowProps {
  title: string;
  children: React.ReactNode;
}

const ProfilesRow = (props: ProfilesRowProps) => {
  const { title, children } = props;
  return (
    <div className="w-full flex flex-col mb-8">
      <div className="text-2xl font-bold text-white pb-2 border-b border-neutral-700 w-full font-raleway">
        {title}
      </div>
      {children}
    </div>
  );
};

export default Profiles;
